import React, { FC, Fragment, useMemo, useState } from 'react';
import { TravellerFields } from '../../../../types';
import { useTheme } from '../../../../../theme';
import { useTranslation } from 'react-i18next';
import { CdnIcon } from '@shared/ui';
import ModalX from '../../../../../BaseComponents/ModalX';
import cn from 'classnames';
import { LocationSuggestionFragment } from '@websky/graphql';
import { useDirections } from '../../hooks';
import DropdownDirections from '../DropdownDirections/DropdownDirections';
import ModalFullscreen from '../../../../../ModalFullscreen';

interface Props {
	cities?: LocationSuggestionFragment[];
}

const MobileDirections: FC<Props> = ({ cities }) => {
	const { MobileDirections: css } = useTheme('TravellerForm');

	const { t } = useTranslation('Traveller');

	const { fields, ...model } = useDirections(cities);

	const [selectedDirection, setSelectedDirection] = useState<TravellerFields | null>(null);
	const [citiesModal, setCitiesModal] = useState(false);

	const direction = useMemo(() => fields.find(field => field.arrayFieldName === selectedDirection), [
		fields,
		selectedDirection
	]);

	return (
		<div className={css.directions}>
			<div className={css.menu}>
				{fields.map((field, index) => (
					<Fragment key={field.arrayFieldName}>
						<div className={css.menu__item} onClick={() => setSelectedDirection(field.arrayFieldName)}>
							<div className={css.menu__labelWrapper}>
								<CdnIcon icon={field.icon} className={css.flightIcon} />
								<span>{field.label}</span>
							</div>
							<CdnIcon icon="Action/Edit" className={css.editIcon} />
						</div>

						{index === 0 && <div className={css.horizontalLine} />}
					</Fragment>
				))}
			</div>

			<ModalFullscreen
				classes={{
					paper: css.directionsModal__paper
				}}
				className={css.directionsModal__content}
				ignoreClose={false}
				isOpen={!!direction}
				onClose={() => setSelectedDirection(null)}
				title={<div className={css.directionsModal__title}>{direction?.label || ''}</div>}
			>
				<div className={css.directionsModal__directions}>
					<div
						className={cn(css.directionsModal__header, {
							[css.directionsModal__header_withLine]: direction?.selectedOptions?.length > 0
						})}
						onClick={() => setCitiesModal(true)}
					>
						<span>{t('Directions')}</span>
						<CdnIcon className={css.addIcon} icon="Action/Add" />
					</div>

					{direction?.selectedOptions?.map(option => (
						<div
							key={option.value}
							className={css.directionsModal__selectedDirections}
							onClick={() => model.deleteOption(direction?.arrayFieldName, option)}
						>
							<span>{option.label}</span>
							<CdnIcon className={css.removeIcon} icon="Action/Remove" />
						</div>
					))}
				</div>
			</ModalFullscreen>

			<ModalX
				classes={{
					paper: css.dropdownModal__paper,
					closeButton: css.dropdownModal__closeButton,
					content: css.dropdownModal__content
				}}
				isOpen={citiesModal && !!direction}
				onClose={() => setCitiesModal(false)}
				noPadding
			>
				{!!direction && (
					<DropdownDirections
						{...model}
						field={direction}
						open
						forcePopupIcon={false}
						InputProps={{
							variant: 'labelIn',
							classes: {
								labelIn: css.dropdownInput__labelIn
							}
						}}
						classes={{
							root: css.dropdown__root,
							listbox: css.dropdown__listbox,
							paper: css.dropdown__paper,
							popper: css.dropdown__popper,
							option: css.dropdown__option
						}}
					/>
				)}
			</ModalX>
		</div>
	);
};

export default MobileDirections;
